import { useState } from 'react';
import type { AvatarConfig, AvatarPartCategory } from '@flip7/shared';
import {
  SKIN_PALETTES,
  HAIR_COLORS,
  HAIR_STYLES,
  EYE_STYLES,
  MOUTH_STYLES,
  HAT_STYLES,
  GLASSES_STYLES,
  FACIAL_HAIR_STYLES,
  randomAvatarConfig,
} from '@flip7/shared';
import { Avatar } from '../components/Avatar';

interface AvatarEditorScreenProps {
  initialConfig: AvatarConfig | null;
  onSave: (config: AvatarConfig) => void;
  onCancel: () => void;
}

const CATEGORIES: { key: AvatarPartCategory; label: string }[] = [
  { key: 'skin', label: 'Skin' },
  { key: 'hairStyle', label: 'Hair' },
  { key: 'hairColor', label: 'Color' },
  { key: 'eyes', label: 'Eyes' },
  { key: 'mouth', label: 'Mouth' },
  { key: 'hat', label: 'Hat' },
  { key: 'glasses', label: 'Glasses' },
  { key: 'facialHair', label: 'Facial' },
];

export function AvatarEditorScreen({ initialConfig, onSave, onCancel }: AvatarEditorScreenProps) {
  const [config, setConfig] = useState<AvatarConfig>(initialConfig ?? randomAvatarConfig());
  const [activeCategory, setActiveCategory] = useState<AvatarPartCategory>('skin');

  const update = (key: keyof AvatarConfig, value: number | string | null) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  function renderOptions() {
    switch (activeCategory) {
      case 'skin':
        return SKIN_PALETTES.map((palette, i) => (
          <button
            key={i}
            onClick={() => update('skin', i)}
            className={`avatar-editor__color-swatch${config.skin === i ? ' avatar-editor__color-swatch--active' : ''}`}
            style={{ background: palette.base }}
          />
        ));
      case 'hairStyle':
        return HAIR_STYLES.map((name, i) => (
          <button key={i} className={`btn btn-small ${config.hairStyle === i ? 'btn-primary' : 'btn-secondary'}`} onClick={() => update('hairStyle', i)}>
            {name}
          </button>
        ));
      case 'hairColor':
        return HAIR_COLORS.map((color, i) => (
          <button
            key={i}
            onClick={() => update('hairColor', i)}
            className={`avatar-editor__color-swatch${config.hairColor === i ? ' avatar-editor__color-swatch--active' : ''}`}
            style={{ background: color }}
          />
        ));
      case 'eyes':
        return EYE_STYLES.map((name, i) => (
          <button key={i} className={`btn btn-small ${config.eyes === i ? 'btn-primary' : 'btn-secondary'}`} onClick={() => update('eyes', i)}>
            {name}
          </button>
        ));
      case 'mouth':
        return MOUTH_STYLES.map((name, i) => (
          <button key={i} className={`btn btn-small ${config.mouth === i ? 'btn-primary' : 'btn-secondary'}`} onClick={() => update('mouth', i)}>
            {name}
          </button>
        ));
      case 'hat':
        return HAT_STYLES.map((name, i) => (
          <button key={i} className={`btn btn-small ${(i === 0 ? config.hat === null : config.hat === i - 1) ? 'btn-primary' : 'btn-secondary'}`} onClick={() => update('hat', i === 0 ? null : i - 1)}>
            {name}
          </button>
        ));
      case 'glasses':
        return GLASSES_STYLES.map((name, i) => (
          <button key={i} className={`btn btn-small ${(i === 0 ? config.glasses === null : config.glasses === i - 1) ? 'btn-primary' : 'btn-secondary'}`} onClick={() => update('glasses', i === 0 ? null : i - 1)}>
            {name}
          </button>
        ));
      case 'facialHair':
        return FACIAL_HAIR_STYLES.map((name, i) => (
          <button key={i} className={`btn btn-small ${(i === 0 ? config.facialHair === null : config.facialHair === i - 1) ? 'btn-primary' : 'btn-secondary'}`} onClick={() => update('facialHair', i === 0 ? null : i - 1)}>
            {name}
          </button>
        ));
    }
  }

  return (
    <div className="avatar-editor">
      {/* Preview */}
      <div className="avatar-editor__preview">
        <Avatar config={config} size={64} border={config.border} />
        <div><Avatar config={config} size={36} /></div>
        <div><Avatar config={config} size={24} /></div>
      </div>

      {/* Category tabs */}
      <div className="avatar-editor__categories">
        {CATEGORIES.map(cat => (
          <button
            key={cat.key}
            className={`btn btn-small ${activeCategory === cat.key ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveCategory(cat.key)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Options */}
      <div className="avatar-editor__options">
        {renderOptions()}
      </div>

      {/* Actions */}
      <div className="avatar-editor__actions">
        <button className="btn btn-secondary btn-small" onClick={() => setConfig(randomAvatarConfig())}>
          Randomize
        </button>
        <button className="btn btn-secondary btn-small" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn btn-primary btn-small" onClick={() => onSave(config)}>
          Save
        </button>
      </div>
    </div>
  );
}
